import type { ReferenceImageUrlHints } from './referenceImageUrl'
import { extensionForImageMime, fetchRemoteImageBlob, imageUrlShouldIncludeCredentials } from './remoteImageBlob'
import { resolveMediaImage } from './resolvedMediaImage'

function sanitizeBaseName(raw: string): string {
  const s = raw.trim().slice(0, 120)
  return s.replace(/[/\\?*:|"<>]/g, '_').replace(/\s+/g, ' ') || `glyphic_${Date.now()}`
}

/** 同源或可 CORS 拉取后以 Blob 触发下载；外链无 CORS 时退回新标签打开原图。 */
export type BrowserImageDownloadResult = 'blob_download' | 'navigation_fallback'

/**
 * 触发浏览器下载单张图片。优先同源/可 CORS 的 fetch blob；外链无 CORS 时回落为「新开标签」（若仍被拒绝则交由调用方 Toast）。
 */
export async function triggerBrowserImageDownload(
  rawSrc: string,
  suggestedName?: string,
  hints?: ReferenceImageUrlHints,
): Promise<BrowserImageDownloadResult> {
  const media = resolveMediaImage(rawSrc, hints)
  const src = media.displayUrl
  if (!src) {
    throw new Error('图片地址为空')
  }
  const base = sanitizeBaseName(suggestedName ?? '')

  if (src.startsWith('blob:') || src.startsWith('data:')) {
    const blob = await fetchRemoteImageBlob(src, hints)
    const ext = extensionForImageMime(blob.type || 'image/png')
    const name = `${base || `glyphic_${Date.now()}`}.${ext}`
    await downloadBlob(blob, name)
    return 'blob_download'
  }

  try {
    const blob = await fetchRemoteImageBlob(src, hints)
    const ext = extensionForImageMime(blob.type || 'image/png')
    const name = `${base || `glyphic_${Date.now()}`}.${ext}`
    await downloadBlob(blob, name)
    return 'blob_download'
  } catch {
    const a = document.createElement('a')
    a.href = src
    a.target = '_blank'
    a.rel = imageUrlShouldIncludeCredentials(src) ? 'noopener' : 'noopener noreferrer'
    a.download = ''
    document.body.appendChild(a)
    a.click()
    a.remove()
    return 'navigation_fallback'
  }
}

export function toastForBrowserImageDownload(mode: BrowserImageDownloadResult): {
  message: string
  variant: 'success' | 'info'
  autoCloseMs: number
} {
  if (mode === 'blob_download') {
    return {
      message: '下载流程已执行：文件写入完成后，请在系统「下载」文件夹或浏览器下载栏中查看',
      variant: 'success',
      autoCloseMs: 4800,
    }
  }
  return {
    message:
      '受跨域限制，页面无法直接打包该图片；已尝试在新标签页打开原图。请在新页面使用「另存为」保存，或从浏览器下载记录中查看',
    variant: 'info',
    autoCloseMs: 5600,
  }
}

function downloadBlob(blob: Blob, filename: string): Promise<void> {
  return new Promise((resolve, reject) => {
    try {
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      a.rel = 'noopener'
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.setTimeout(() => {
        URL.revokeObjectURL(url)
        resolve()
      }, 400)
    } catch (e) {
      reject(e instanceof Error ? e : new Error(String(e)))
    }
  })
}
